/**
 * Brand for the active-org segment that prefixes every org-scoped query
 * key. In production this is always the caller's active organization id —
 * RBAC is always on, and `useAuth()` resolves a membership before any
 * org-scoped page renders. The `'global'` constant below exists only as
 * a defensive bootstrap-time sentinel: it is the value `orgScope(null)`
 * returns during the brief window before `useAuth()` has resolved the
 * first membership, so React Query keys remain non-empty strings during
 * that gap. It should never reach a fetch under normal flow.
 *
 * Deleting this sentinel means gating every `useOrgKey()` consumer behind an
 * auth-ready guard so the helper can return `string | undefined` instead. That
 * is ~50 call sites across `web/src/**` and the plugin web hooks, so it is a
 * change of its own rather than a drive-by.
 *
 * `useOrgKey()` (`@reflex/ui/client/context`) is the canonical way to obtain
 * an `OrgScope` value inside React components.
 */
export type OrgScope = string;
export const GLOBAL_ORG_SCOPE: OrgScope = 'global';

/**
 * Normalise an org id (or null/undefined) into an `OrgScope` segment that
 * is always a non-empty string, so it produces a stable key. Returns
 * {@link GLOBAL_ORG_SCOPE} only during the bootstrap gap — see the type
 * doc above. Pure helper — no React context.
 */
export function orgScope(orgId: string | null | undefined): OrgScope {
  return orgId ?? GLOBAL_ORG_SCOPE;
}

/**
 * The agent-list facets the server applies itself (`GET /agents`), as opposed
 * to the ones the client narrows in memory. Every field here is part of the
 * query key, because each one produces a different paginated result set: a
 * page of "runs in the payments group" is not a subset of page one of the
 * unfiltered list.
 *
 * Pushing these down is what makes a filtered list complete. Filtering in
 * memory over a 50-row page silently drops every match older than that page,
 * which reads as "the list lost my agents"; filtering in SQL paginates over
 * the matching rows instead, at the same cost per page.
 */
export interface AgentListQueryFilters {
  userId?: string | null;
  /** Expanded status list — the UI's status *group*, resolved to statuses. */
  statuses?: readonly string[] | null;
  mark?: string | null;
  groupId?: string | null;
  labelId?: string | null;
}

/**
 * The filters that actually narrow, or `undefined` when none do — so an
 * unfiltered list keeps the bare `['agents', org]` key it has always had and
 * a caller passing all-nulls shares that cache entry rather than minting a
 * near-duplicate one.
 *
 * Entries are sorted so the result is canonical: two callers that build the
 * same filters in a different field order produce an identical object, which
 * makes the key safe to serialise and memoise on, not just to hash.
 */
export function normalizeAgentListFilters(
  filters: AgentListQueryFilters | undefined,
): Record<string, unknown> | undefined {
  if (!filters) return undefined;
  const entries = Object.entries(filters)
    .filter(([, value]) => value !== null && value !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : 1));
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

export const queryKeys = {
  /**
   * Agents list (optionally narrowed by the server-side facets). The `org`
   * segment guarantees cross-org data never collides in the cache when the
   * active org is switched mid-session, and every `['agents', org]` entry
   * stays prefix-matchable so the WS eviction paths reach all of them.
   */
  agents: (org: OrgScope, filters?: AgentListQueryFilters) => {
    const active = normalizeAgentListFilters(filters);
    return active ? (['agents', org, active] as const) : (['agents', org] as const);
  },
  /**
   * Pinned agents (`GET /agents?pinned=true`). A dedicated, non-paginated
   * query so a pinned run surfaces in the sidebar even when it is old enough
   * to sit past the pages the infinite-scroll `agents` list has loaded.
   * Keyed alongside the same optional `userId` scope as {@link agents} so the
   * Owner filter keeps its own slot.
   */
  pinnedAgents: (org: OrgScope, userId?: string | null) =>
    userId ? (['pinned-agents', org, { userId }] as const) : (['pinned-agents', org] as const),
  /**
   * Server-side agent-run search (`GET /agents?search=`). Keyed by the
   * trimmed query so each term keeps its own slot and
   * `placeholderData: keepPreviousData` renders a smooth transition as the
   * user types in the `@`-mention run picker.
   */
  agentSearch: (org: OrgScope, search: string) => ['agent-search', org, search] as const,
  agent: (org: OrgScope, id: string) => ['agent', org, id] as const,
  agentDrafts: (org: OrgScope) => ['agent-drafts', org] as const,
  /**
   * The org's agent groups (`GET /agent-groups`). Membership itself rides on
   * each agent's associations, so this key only carries the group list and
   * its member counts; changing membership invalidates the agent lists too.
   */
  agentGroups: (org: OrgScope) => ['agent-groups', org] as const,
  /**
   * The org's label catalog (`GET /agent-labels`). Reading it is also what
   * seeds a new organization's built-in vocabulary, so it is fetched even by
   * surfaces that only render labels rather than manage them.
   */
  agentLabels: (org: OrgScope) => ['agent-labels', org] as const,
  /**
   * The signed-in user's per-agent "last viewed" timestamps, backing the
   * sidebar's unseen-changes dot (`GET /agents/views`). Org-scoped so switching
   * orgs evicts the previous org's slot, matching {@link agents}.
   */
  agentViews: (org: OrgScope) => ['agent-views', org] as const,
  agentDraft: (org: OrgScope, id: string) => ['agent-draft', org, id] as const,
  /** Stream events are inherently per-agent — already isolated, no `org`. */
  stream: (agentId: string) => ['stream', agentId] as const,
  /**
   * The loaded window's page info for a paged stream (`null` for legacy
   * unindexed agents). Written by the stream query and page-back merges,
   * read by the socket hook (afterSequence) and the load-earlier UI.
   */
  streamPage: (agentId: string) => ['stream-page', agentId] as const,
  /**
   * The agent's complete event history (`GET /agents/:id/stream` with no
   * paging params — the server answers with the full array regardless of
   * indexing). Separate from {@link stream}, whose entry holds only the
   * loaded window for paged agents: consumers whose semantics need every
   * event (plugin tabs, replay, boot-time signals) read this slot.
   */
  streamFull: (agentId: string) => ['stream-full', agentId] as const,
  /** The agent's session index (`/agents/:id/session-index`) — nav-rail turns. */
  sessionIndex: (agentId: string) => ['session-index', agentId] as const,
  /**
   * Live Runloop devbox status for an agent (`/agents/:id/devbox-status`).
   * Polled by the sandbox status badge while an agent is in view so the
   * indicator reflects the *real* devbox lifecycle (e.g. an auto-suspended
   * box) rather than a state inferred from the agent's status alone.
   * Per-agent and already isolated, so no `org` segment.
   */
  devboxStatus: (agentId: string) => ['devbox-status', agentId] as const,
  /**
   * Live on-box service statuses (`/agents/:id/services/status`) — the
   * registered daemons' persisted lifecycle status plus a pid liveness
   * probe. Polled while a servers surface (detail panel section, header
   * chip popover) is open. Per-agent, so no `org` segment.
   */
  serviceStatuses: (agentId: string) => ['service-statuses', agentId] as const,
  /** One service's on-box log file (`/agents/:id/services/:serviceId/logs`). */
  serviceLog: (agentId: string, serviceId: string) => ['service-log', agentId, serviceId] as const,
  agentQueue: (agentId: string) => ['agent', agentId, 'queue'] as const,
  blueprints: (org: OrgScope) => ['blueprints', org] as const,
  blueprint: (org: OrgScope, id: string) => ['blueprint', org, id] as const,
  blueprintLogs: (org: OrgScope, id: string) => ['blueprint-logs', org, id] as const,
  snapshots: (org: OrgScope) => ['snapshots', org] as const,
  snapshotStatus: (org: OrgScope, id: string) => ['snapshot-status', org, id] as const,
  baseImage: (org: OrgScope) => ['base-image', org] as const,
  /**
   * Health of the org's stored Runloop (sandbox provider) key — whether it
   * actually works, not just whether it's set. Polled by the setup gate to
   * re-route a bootstrapped org with a revoked / missing / trial key back
   * into the wizard. Org-scoped so switching orgs evicts the prior result.
   */
  sandboxHealth: (org: OrgScope) => ['sandbox-health', org] as const,
  /** Org-scoped users list (members visible to the caller). */
  users: (org: OrgScope) => ['users', org] as const,
  user: (id: string) => ['user', id] as const,
  /** Org-scoped teams list (`GET /organizations/:id/teams`, `teams:read`). */
  orgTeams: (org: OrgScope) => ['org-teams', org] as const,
  /**
   * A single organization by id. The id is itself the org scope (org-path
   * route `GET /organizations/:id`), so the key is naturally per-org.
   */
  organization: (id: string) => ['organization', id] as const,
  /**
   * Pending invites for one organization (`GET /organizations/:id/invites`,
   * `members:invite`). Like {@link organization} the id is itself the scope,
   * because the route is org-path rather than active-org. One slot per org
   * rather than per calling surface, so an invite minted from the org-facing
   * members page and one minted from the platform admin's organization detail
   * page share a cache entry.
   */
  organizationInvites: (id: string) => ['organizations', id, 'invites'] as const,
  userProviders: ['user', 'providers'] as const,
  userProvidersById: (id: string) => ['users', id, 'providers'] as const,
  /** Linked OAuth identities for the signed-in user (Settings → Connected accounts). */
  userIdentities: ['user', 'identities'] as const,
  /**
   * User-tier credentials (claude-max, etc.) — /me/credentials/*. Per
   * the org-scoped secrets table, a user holds a disjoint credential
   * set in each org they're a member of. The `org` segment keeps the
   * TanStack cache slots distinct so org switching evicts the
   * previous org's data instead of flashing stale credentials.
   */
  userCredentials: (org: OrgScope) => ['user', 'credentials', org] as const,
  claudeMaxStatus: (org: OrgScope) => ['user', 'credentials', 'claude-max', 'status', org] as const,
  codexSubscriptionStatus: (org: OrgScope) =>
    ['user', 'credentials', 'codex-subscription', 'status', org] as const,
  /**
   * User-tier general-purpose secrets — /me/secrets/*. Same per-org
   * tenancy as credentials above.
   */
  userSecrets: (org: OrgScope) => ['user', 'secrets', org] as const,
  /**
   * Model-provider secrets (`mps_*` wrappers). The user list is the
   * caller's personal provider keys; the team list is per-team.
   * Both carry the `org` segment because the server reads happen
   * inside the active organization's context — switching orgs must
   * evict the previous slot the same way it does for `userSecrets`
   * and team secrets.
   */
  myModelProviderSecrets: (org: OrgScope) => ['user', 'model-provider-secrets', org] as const,
  teamModelProviderSecrets: (org: OrgScope, teamId: string) =>
    ['team', 'model-provider-secrets', org, teamId] as const,
  /**
   * Org-tier model-provider secrets (`mps_*` wrappers owned by the
   * organization). Carries the `org` segment so switching orgs evicts
   * the previous org's keys, mirroring the user/team slots above.
   */
  orgModelProviderSecrets: (org: OrgScope) => ['org', 'model-provider-secrets', org] as const,
  /**
   * Org-scoped LLM task assignments — the per-org override of the
   * platform default model/key for platform LLM tasks (auto-titling
   * today). Carries the `org` segment so an org switch evicts the
   * previous org's assignments, matching the secrets-tier slots above.
   */
  orgLlmTaskAssignments: (org: OrgScope) => ['org', 'llm-task-assignments', org] as const,
  /**
   * Every secret the caller can use, folded across tiers. `types` is
   * sorted into the key so the launch picker (apiKey+subscription) and
   * the chat picker (apiKey) keep distinct cache slots while two callers
   * requesting the same set share one.
   */
  accessibleModelProviderSecrets: (org: OrgScope, types: readonly string[], owner?: string) =>
    [
      'user',
      'model-provider-secrets',
      'accessible',
      org,
      [...types].sort().join(','),
      owner ?? '',
    ] as const,
  /**
   * Never queried, and nothing should invalidate it. `/auth/me` is read by
   * the host's `useAuth`, which holds the session in its own reducer rather
   * than React Query, so there is no cache entry here to serve or mark
   * stale — its `refreshSession()` is the only thing that re-reads the session
   * (`web/src/hooks/auth/useSessionRefresh.ts`). Retained because this key
   * set is plugin surface with a higher removal bar, and
   * `query-keys-parity.test.ts` pins the two copies together.
   */
  authMe: ['auth', 'me'] as const,
  /** Pending invites addressed to the signed-in user's email. */
  pendingInvites: ['invites', 'pending'] as const,
  config: ['config'] as const,
  /** Platform maintenance-mode state for the admin panel (`/admin/maintenance`). */
  maintenance: ['admin', 'maintenance'] as const,
  /** Deployment-wide Reflex settings managed from the platform admin panel. */
  serviceSettings: ['admin', 'service-settings'] as const,
  /** Read-only transactional email configuration shown in the admin panel. */
  emailSettings: ['admin', 'email-settings'] as const,
  plugins: ['plugins'] as const,
  agentModelSupport: (org: OrgScope) => ['config', 'agent-model-support', org] as const,
  agentModelPolicy: (org: OrgScope) => ['admin', 'agent-model-policy', org] as const,
  pluginHealth: (name: string) => ['plugin-health', name] as const,
  flags: ['flags'] as const,
  /** Per-user account-scoped feature flag overrides — `/flags/account`. */
  accountFlags: ['flags', 'account'] as const,
  flagOverrides: (key: string) => ['flag-overrides', key] as const,
  authSettings: ['auth', 'settings'] as const,
  /**
   * Secrets discovery is org-scoped on the server; a stale flash on org
   * switch would briefly suggest secrets exist that the new org cannot
   * reach. Encode the org segment to keep cache slots distinct.
   */
  secrets: (org: OrgScope) => ['secrets', org] as const,
  /**
   * Cache slot for `GET /secrets/available`. `ownerSignature` is a
   * stringified owner context so a launch-dialog query and a team-owned
   * persona's query don't collide in the cache even when the org and
   * name filter match.
   */
  secretsAvailable: (org: OrgScope, name: string, ownerSignature: string) =>
    ['secrets', org, 'available', name, ownerSignature] as const,
  secretsStatus: ['secrets', 'status'] as const,
  secretManagerStatus: ['secrets', 'manager'] as const,
  serviceSecretRegistry: ['service-secrets', 'registry'] as const,
  chatModels: ['chat', 'models'] as const,
  adminUsers: ['admin', 'users'] as const,
  /**
   * Cache slot for one page of the paginated admin users list.
   * See `web/src/lib/query-keys.ts` for the full rationale.
   */
  adminUsersPage: (params: { page: number; pageSize: number }) =>
    ['admin', 'users', params] as const,
  adminUserOrganizations: (id: string) => ['admin', 'user', id, 'organizations'] as const,
  adminAppAdmins: ['admin', 'app-admins'] as const,
  adminOrganizations: ['admin', 'organizations'] as const,
  /**
   * Cache slot for one page of the paginated admin organizations list.
   * See `web/src/lib/query-keys.ts` for the full rationale.
   */
  adminOrganizationsPage: (params: { page: number; pageSize: number; q: string }) =>
    ['admin', 'organizations', params] as const,
  adminOrganization: (id: string) => ['admin', 'organization', id] as const,
  /**
   * Platform-admin enterprise-identity setup status for one org
   * (`GET /admin/organizations/:id/enterprise-identity`). Kept in parity
   * with `web/src/lib/query-keys.ts`; see `query-keys-parity.test.ts`.
   */
  adminOrganizationEnterpriseIdentity: (id: string) =>
    ['admin', 'organization', id, 'enterprise-identity'] as const,
  /** Billing entities — commercial owners of organizations (`GET /admin/billing-entities`). */
  adminBillingEntities: ['admin', 'billing-entities'] as const,
  adminBillingEntity: (id: string) => ['admin', 'billing-entity', id] as const,
  /**
   * The billing entity that owns one org, and that billing entity's organizations
   * (`GET /admin/organizations/:id/billing-entity`). Platform-admin only: the
   * organization resource itself carries no billing entity. Kept in parity with
   * `web/src/lib/query-keys.ts`; see `query-keys-parity.test.ts`.
   */
  adminOrganizationBillingEntity: (id: string) =>
    ['admin', 'organization', id, 'billing-entity'] as const,
  adminOrganizationMembers: (id: string) => ['admin', 'organization', id, 'members'] as const,
  adminOrganizationTeams: (id: string) => ['admin', 'organization', id, 'teams'] as const,
  adminOrganizationEmailDomains: (id: string) =>
    ['admin', 'organization', id, 'email-domains'] as const,
  adminOrganizationActivity: (id: string) => ['admin', 'organization', id, 'activity'] as const,
  serverStatus: ['server-status'] as const,
  adminServerStatus: ['server-status', 'admin'] as const,
  webhookActivity: ['webhook-activity'] as const,
  /** Recent agent activity is derived from the org-scoped agents list. */
  recentActivity: (org: OrgScope, limit: number) => ['activity', 'recent', org, limit] as const,
  /** Profile stats aggregate the user's agents under the active org. */
  profileStats: (org: OrgScope, userId: string, year: number) =>
    ['profile', 'stats', org, userId, year] as const,
  defaultTheme: ['settings', 'default-theme'] as const,
  customThemes: ['settings', 'custom-themes'] as const,
  tweakcnCatalog: ['settings', 'tweakcn-catalog'] as const,
  onboardingState: ['onboarding', 'state'] as const,
} as const;
